import { RefreshCw } from 'lucide-react';

import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { StatusBadge, type StatusVariant } from '@/shared/components/status-badge';
import { formatDateTime, formatNumber, formatRelativeDate } from '@/shared/lib/format';
import { DEFAULT_TIME_ZONE } from '@/shared/lib/time-zone';

import { useProviderStockSyncs, useRequestProviderStockRefresh } from '../api/providers.queries';
import type { ProviderStockSync } from '../types/provider.types';

/** Three failed runs in a row is when the worker logs an error; the panel says so too. */
const FAILING_AFTER = 3;

function syncState(sync: ProviderStockSync): { label: string; variant: StatusVariant } {
  if (sync.isRunning) return { label: 'Checking now', variant: 'info' };
  if (sync.consecutiveFailures >= FAILING_AFTER)
    return { label: `Failing (${sync.consecutiveFailures} runs)`, variant: 'danger' };
  if (sync.consecutiveFailures > 0) return { label: 'Last check incomplete', variant: 'warning' };
  if (!sync.lastSucceededAtUtc) return { label: 'Not checked yet', variant: 'neutral' };
  return { label: 'Up to date', variant: 'success' };
}

/**
 * Stock for a provider whose stock the fulfilment worker reads (LikeCard): the
 * last full check, whether it is failing, and how many items are in or out of
 * stock. A failing check never marks items out of stock; it leaves the last
 * answers until they lapse. Hidden for providers that are not checked
 * (Mintroute has no stock read; checkout reserves instead).
 */
export function StockSyncPanel({
  providerId,
  canManage,
}: {
  providerId: number;
  canManage: boolean;
}) {
  const syncs = useProviderStockSyncs();
  const refresh = useRequestProviderStockRefresh();
  const sync = syncs.data?.find((s) => s.vendorId === providerId);
  if (!sync) return null;

  const state = syncState(sync);
  const requested = sync.refreshRequestedAtUtc !== null;

  return (
    <Card>
      <CardHeader className="flex flex-row items-center justify-between gap-2">
        <CardTitle className="flex items-center gap-2 text-sm">
          Stock
          <StatusBadge variant={state.variant}>{state.label}</StatusBadge>
        </CardTitle>
        {canManage && (
          <Button
            size="sm"
            variant="outline"
            disabled={refresh.isPending || requested || sync.isRunning}
            onClick={() => refresh.mutate(providerId)}
          >
            <RefreshCw className="size-3.5" aria-hidden />
            {requested ? 'Check requested' : 'Check stock now'}
          </Button>
        )}
      </CardHeader>
      <CardContent className="space-y-3 text-sm">
        <dl className="grid grid-cols-3 gap-3">
          <div>
            <dt className="text-muted-foreground text-xs">In stock</dt>
            <dd className="font-medium">{formatNumber(sync.inStockCount)}</dd>
          </div>
          <div>
            <dt className="text-muted-foreground text-xs">Out of stock</dt>
            <dd className="font-medium">{formatNumber(sync.outOfStockCount)}</dd>
          </div>
          <div>
            <dt className="text-muted-foreground text-xs">Not known</dt>
            <dd className="font-medium">{formatNumber(sync.unknownCount)}</dd>
          </div>
        </dl>
        <p className="text-muted-foreground text-xs">
          Last full check {formatRelativeDate(sync.lastSucceededAtUtc)}
          {sync.lastSucceededAtUtc
            ? ` (${formatDateTime(sync.lastSucceededAtUtc, DEFAULT_TIME_ZONE)})`
            : ''}
          {sync.nextRunAtUtc
            ? ` · next ${formatDateTime(sync.nextRunAtUtc, DEFAULT_TIME_ZONE)}`
            : ''}
          {sync.lastChangedCount ? ` · ${sync.lastChangedCount} changed in the last check` : ''}
        </p>
        {sync.consecutiveFailures > 0 && (
          <p className="text-status-warning text-xs">
            The last check could not read {formatNumber(sync.lastFailedCount)} of{' '}
            {formatNumber(sync.lastCheckedCount)} items
            {sync.lastError ? ` (${sync.lastError})` : ''}. Those items keep their last answer;
            checkout still checks the provider before every payment.
          </p>
        )}
      </CardContent>
    </Card>
  );
}
